import { useUserContext } from '@/contexts/userContext';
import { Box, Button, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { isEmail } from 'validator';
import ErrorMessage from '../errorMessage';
import TextField from '../textField';

type Props = {};
const SignInForm = ({}: Props) => {
  const userContext = useUserContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const appId = searchParams.get('appId');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [apiError, setApiError] = useState<any>();

  const handleSubmit = async () => {
    setApiError(null);
    const errors = { email: '', password: '' };
    let hasError = false;
    try {
      if (!email) {
        errors['email'] = 'Required';
        hasError = true;
      }
      if (!isEmail(email)) {
        errors['email'] = 'Please enter valid email';
        hasError = true;
      }
      if (!password) {
        errors['password'] = 'Required';
        hasError = true;
      }
      if (!hasError && userContext) {
        setLoading(true);
        await userContext.signIn(email, password);
        const redirect = searchParams.get('redirect');
        if (redirect) {
          navigate(redirect);
        } else {
          navigate(appId ? `/userApp/${appId}` : '/dashboard');
        }
      }
    } catch (error: any) {
      setApiError(error);
    }
    setLoading(false);
    setErrors(errors);
  };

  const handleSignUp = () => navigate('/signup');
  const handleForgotPassword = () => navigate('/forgotPassword');
  const handleCancel = () => navigate('/');

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden', bgcolor: 'background.paper' }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Welcome back
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Sign in to your account
        </Typography>
        {apiError && (
          <Box sx={{ mt: 1 }}>
            <ErrorMessage error={apiError} />
          </Box>
        )}
      </Box>

      {/* Fields */}
      <Box sx={{ px: 3, py: 2.5 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              value={email}
              label={'Email Address'}
              required
              error={errors['email']}
              onChange={setEmail}
              type="email"
              maxLength={100}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              value={password}
              label={'Password'}
              required
              error={errors['password']}
              onChange={setPassword}
              type="password"
              maxLength={100}
            />
          </Grid>
          <Grid container size={12}>
            <Grid size="grow">
              <Link href="#" variant="body2" onClick={handleForgotPassword} sx={{ fontWeight: 600 }}>
                Forgot password?
              </Link>
            </Grid>
            <Grid>
              <Link href="#" variant="body2" onClick={handleSignUp} sx={{ fontWeight: 600 }}>
                {"Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Action bar */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button
          variant="text"
          size="small"
          onClick={handleCancel}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          size="small"
          color="secondary"
          variant="contained"
          onClick={handleSubmit}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          Sign In
        </Button>
      </Box>

      {/* SSO section */}
      <Box sx={{ px: 3, pb: 2.5 }}>
        <Divider sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            or continue with
          </Typography>
        </Divider>
        <Stack spacing={2} alignItems="center">
          <GoogleLogin
            onSuccess={async (credentialResponse) => {
              const credential = credentialResponse.credential;
              if (credential && userContext && userContext.signInWithGoogle) {
                try {
                  setLoading(true);
                  setApiError(null);
                  await userContext.signInWithGoogle(credential);
                  const redirect = searchParams.get('redirect');
                  if (redirect) {
                    navigate(redirect);
                  } else {
                    navigate(appId ? `/userApp/${appId}` : '/dashboard');
                  }
                } catch (error: any) {
                  setApiError(error);
                } finally {
                  setLoading(false);
                }
              }
            }}
            onError={() => {
              setApiError('Google Sign-In failed');
            }}
            text="signin_with"
            theme="outline"
            size="large"
            width="280"
            shape="rectangular"
          />
          <Button
            variant="outlined"
            disabled
            sx={{
              textTransform: 'none',
              borderRadius: '4px',
              width: '280px',
              height: '40px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            Sign In with your organization SSO
          </Button>
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              By signing in, you agree to our{' '}
              <Link href="/terms" sx={{ fontWeight: 600 }}>
                Terms & Conditions
              </Link>{' '}
              and{' '}
              <Link href="/privacy" sx={{ fontWeight: 600 }}>
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default SignInForm;
